import { isEqual, cloneDeep } from "lodash-es";
import { CompoundUtils } from "./compound-utils";
import { CompoundRender } from "./compound-render";

import { console } from "#/log";
import { Compounds } from "#/declarations";

type MoleculePropertyName = keyof Compounds.Molecule;

/**
 * Contains static functions for mutating a render / assign tree into
 * a flattened Compounds.Structure.  The only public function to this
 * class is `buildTree` which creates a Compounds.Structure from a
 * render tree.
 */
export class CompoundTree {
  /**
   * Adds a unique molecule ID, and the provided range ID to the build item.
   * TODO: Maybe rename for better accuracy? Or break out showHydrogen
   * though that means a needless extra traversal.
   */
  static addIdsToMolecule(
    molecule: Compounds.Molecule,
    rangeId?: string,
    groupId?: string,
  ): Compounds.Molecule {
    molecule.rangeId = rangeId;
    if (groupId) {
      molecule.groupId = groupId;
    }
    molecule.moleculeId = CompoundUtils.generateUUID();
    if (molecule.assign) {
      for (let i = 0; i < molecule.assign.length; i++) {
        this.addIdsToMolecule(molecule.assign[i].molecule, rangeId, groupId);
      }
    }
    return molecule;
  }

  /**
   * Gets a molecule's parent based on a property name and value.  If the
   * root molecule matches the property name and value it is returned.
   * NOTE: if the match is the root of the tree, the root is returned
   *  instead of the parent.
   */
  static getMoleculeParentByProperty(
    renderTree: Compounds.Molecule,
    propertyName: MoleculePropertyName,
    propertyValue: any,
  ): Compounds.Molecule | null {
    if (
      renderTree.hasOwnProperty(propertyName) &&
      renderTree[propertyName] === propertyValue
    ) {
      return renderTree;
    }
    if (renderTree.assign) {
      for (let i = 0; i < renderTree.assign.length; i++) {
        if (renderTree.assign[i].molecule[propertyName] === propertyValue) {
          return renderTree;
        }
      }
      for (let i = 0; i < renderTree.assign.length; i++) {
        let parent = this.getMoleculeParentByProperty(
          renderTree.assign[i].molecule,
          propertyName,
          propertyValue,
        );
        if (parent) {
          return parent;
        }
      }
    }
    return null;
  }

  /**
   * Traverses the tree to find the molecule that is the parent of moleculeId.
   * If moleculeId is the root moleculeId, then it returns the root molecule.
   */
  static getMoleculeParentById(
    renderTree: Compounds.Molecule,
    moleculeId: string,
  ) {
    return this.getMoleculeParentByProperty(
      renderTree,
      "moleculeId",
      moleculeId,
    );
  }

  /**
   * Retreives a molecule from the render tree based on the provided
   * property name and value.
   */
  static getMoleculeByProperty(
    renderTree: Compounds.Molecule,
    propertyName: MoleculePropertyName,
    propertyValue: any,
  ): Compounds.Molecule | null {
    let matches = this.getMoleculesByProperty(
      renderTree,
      propertyName,
      propertyValue,
    );
    return matches.length > 0 ? matches[0] : null;
  }

  /**
   * Retreives a molecule from the render tree based on the provided
   * property name and value.
   */
  static getMoleculesByProperty(
    renderTree: Compounds.Molecule,
    propertyName: MoleculePropertyName,
    propertyValue: any,
    matches: Array<Compounds.Molecule> = [],
  ): Array<Compounds.Molecule> {
    if (renderTree[propertyName] === propertyValue) {
      matches.push(renderTree);
    }
    if (renderTree.assign) {
      for (let i = 0; i < renderTree.assign.length; i++) {
        matches = this.getMoleculesByProperty(
          renderTree.assign[i].molecule,
          propertyName,
          propertyValue,
          matches,
        );
      }
    }
    return matches;
  }

  /**
   * Traverses the tree to find the molecule with the given moleculeId.
   */
  static getMoleculeById(renderTree: Compounds.Molecule, moleculeId: string) {
    return this.getMoleculeByProperty(renderTree, "moleculeId", moleculeId);
  }

  /**
   * Checks if a given rangeId is in the build.
   */
  static isRangeInBuild(build: Compounds.Molecule, rangeId: string): boolean {
    return (
      CompoundTree.getMoleculesByProperty(build, "rangeId", rangeId).length > 0
    );
  }

  /**
   * Checks if any of a range's build children are in the build
   */
  static areRangeItemsInBuild(build: Compounds.Molecule, range: any) {
    console.debug("compound-tree: is in build", build, range);
    if (range.assigned) {
      for (let id in range.assigned.build) {
        if (CompoundTree.isRangeInBuild(build, range.$key + "-" + id)) {
          return true;
        }
      }
    }
    return false;
  }

  static removeRangeFromBuild(build: Compounds.Molecule, range: any) {
    let newBuild = cloneDeep(build);
    if (range.assigned) {
      for (let id in range.assigned.build) {
        let molecules = this.getMoleculesByProperty(
          newBuild,
          "rangeId",
          range.$key + "-" + id,
        );
        let roots = molecules.filter((m) => m.isBase);
        roots.forEach((m) => {
          // Check if moleculeId is defined before calling the function
          if (m.moleculeId) {
            CompoundTree.removeMoleculeById(newBuild, m.moleculeId);
          }
        });
      }
    }
    return newBuild;
  }

  /**
   * Add an assignment object to a molecule given the molecule ID
   */
  static addAssignment(
    renderTree: Compounds.Molecule,
    moleculeId: string,
    assignment: Compounds.Assignment,
  ): Compounds.Molecule {
    console.debug(
      "compound.tree: add assignment",
      renderTree,
      moleculeId,
      assignment,
    );
    let molecule = this.getMoleculeById(renderTree, moleculeId);
    if (!molecule) {
      throw new Error("Can't find parent " + moleculeId + " to add to");
    }
    if (!molecule.assign) {
      molecule.assign = [];
    }
    molecule.assign.push(assignment);
    return molecule;
  }

  /**
   * Gets the depth of a render tree
   */
  static getDepth(renderTree: Compounds.Molecule): number {
    if (renderTree) {
      let depth = 1;

      // Check if renderTree.assign exists and is an array
      if (renderTree.assign && Array.isArray(renderTree.assign)) {
        let deepestChild = 0;

        for (let assignment of renderTree.assign) {
          let childDepth = this.getDepth(assignment.molecule);
          if (childDepth > deepestChild) {
            deepestChild = childDepth;
          }
        }

        depth += deepestChild;
      }
      return depth;
    }
    return 0;
  }

  /**
   * Remove an assignment object from a render tree given a property and
   * a value.
   */
  static removeMoleculeByProperty(
    renderTree: Compounds.Molecule,
    propertyName: keyof Compounds.Molecule, // Ensure propertyName is a key of Molecule
    propertyValue: any,
  ): Compounds.Molecule | null {
    let molecule = this.getMoleculeParentByProperty(
      renderTree,
      propertyName,
      propertyValue,
    );
    if (molecule) {
      if (molecule.assign) {
        for (let i = 0; i < molecule.assign.length; i++) {
          if (molecule.assign[i].molecule[propertyName] === propertyValue) {
            return molecule.assign.splice(i, 1)[0].molecule;
          }
        }
      }
      // no matches in the assign, remove the root
      renderTree.moleculeId = "base";
      renderTree.structure = "empty";
      renderTree.assign = [];
      delete renderTree.rangeId;
      return renderTree;
    }
    return null;
  }

  /**
   * Remove an assignment object from a render tree given the molecule ID
   */
  static removeMoleculeById(
    renderTree: Compounds.Molecule,
    moleculeId: string,
  ): Compounds.Molecule | null {
    return this.removeMoleculeByProperty(renderTree, "moleculeId", moleculeId);
  }

  static getExpandedTree(tree: Compounds.Molecule): Compounds.Molecule {
    tree.structure = CompoundUtils.getStructure(tree.structure);
    if (tree.assign) {
      for (var i = 0; i < tree.assign.length; i++) {
        this.getExpandedTree(tree.assign[i].molecule);
      }
    }
    return tree;
  }

  /**
   * Checks if two molecule render trees have the same structure
   */
  static isEqual(tree1: Compounds.Molecule, tree2: Compounds.Molecule) {
    let t1 = this.getExpandedTree(tree1);
    let t2 = this.getExpandedTree(tree2);
    if (!isEqual(t1.structure, t2.structure)) {
      // structures aren't equal
      return false;
    }
    if ((t1.assign && !t2.assign) || (!t1.assign && t2.assign)) {
      // one has an assignment, the other doesnt
      return false;
    }
    if (t1.assign && t2.assign) {
      if (t1.assign.length !== t2.assign.length) {
        // different number of assignments
        return false;
      }
      for (let i = 0; i < t1.assign.length; i++) {
        if (!this.isEqual(t1.assign[i].molecule, t2.assign[i].molecule)) {
          // different children in assign
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Creates a Compounds.Structure from a provided render / assignment tree.
   */
  static getStructureFromTree(
    renderTree: Compounds.Molecule,
  ): Compounds.Structure {
    let tree = cloneDeep(renderTree);
    CompoundTree.populateTree(tree);
    let flattened = this.flattenTree(tree);
    return this.makeRenderAssignments(tree, flattened);
  }

  /**
   * Checks if a given moleculeId is found within a tree
   */
  static containsMoleculeId(
    tree: Compounds.Molecule,
    moleculeId: string,
  ): boolean {
    return CompoundTree.getMoleculeById(tree, moleculeId) !== null;
  }

  /**
   * Returns a list of children matching the rangeId of the base of the tree
   */
  public static getRangeChildren(
    tree: Compounds.Molecule,
  ): Array<Compounds.Molecule> {
    let children = this.getChildrenUntil(tree, (molecule) => {
      return (
        molecule.moleculeId !== tree.moleculeId &&
        molecule.hasOwnProperty("isBase") &&
        !!molecule.isBase // Convert to boolean
      );
    });

    return children.filter((d) => d.rangeId === tree.rangeId);
  }

  /**
   * Gets the children of a tree until a specific condition function returns
   * true
   */
  static getChildrenUntil(
    tree: Compounds.Molecule,
    condition: (molecule: Compounds.Molecule) => boolean,
    first: boolean = true,
  ): Array<Compounds.Molecule> {
    let children: Compounds.Molecule[] = [];
    if (!first && !condition(tree)) {
      children.push(tree);
    }

    // Check if 'assign' exists and is an array
    if (Array.isArray(tree.assign) && !condition(tree)) {
      for (let assignment of tree.assign) {
        children.push(
          ...this.getChildrenUntil(assignment.molecule, condition, false),
        );
      }
    }

    return children;
  }

  /**
   * Gets the id of the first bond in a tree.
   * @returns {string} id of the first bond or "" if no bonds present.
   */
  static getFirstBondId(tree: Compounds.Molecule): string {
    let flattened = CompoundTree.getStructureFromTree(tree);
    for (let atom of flattened.atoms) {
      if (atom.name === "R") {
        return CompoundUtils.parseAtomId(atom.id).atomId;
      }
    }
    return "";
  }

  private static addPropertyToStructure(
    structure: Compounds.Structure,
    property: string,
    value: string,
  ) {
    for (let i = 0; i < structure.atoms.length; i++) {
      structure.atoms[i][property] = value;
    }
    for (let i = 0; i < structure.bonds.length; i++) {
      structure.bonds[i][property] = value;
    }
    return structure;
  }

  /**
   * Adds molecule structures if a string structure is provided, adds
   * the highlight and label flags based on the provided options,
   * adds a unique identifier (or range identifier) to each of the molecules,
   * and sets parent molecule IDs to the assignments in the assignment tree.
   * Mutates the tree and runs recursively through all molecules in the
   * assignment tree.
   */
  private static populateTree(
    tree: Compounds.Molecule,
  ): asserts tree is Compounds.HasStructure<Compounds.Molecule> {
    tree.structure = CompoundUtils.getStructure(tree.structure);

    if (tree.groupId) {
      tree.structure = this.addPropertyToStructure(
        tree.structure,
        "groupId",
        tree.groupId,
      );
    }

    if (tree.rangeId) {
      tree.structure = this.addPropertyToStructure(
        tree.structure,
        "rangeId",
        tree.rangeId,
      );
    }

    if (tree.options) {
      CompoundRender.addHighlightFlags(tree.structure, tree.options);
    }

    if (tree.options) {
      CompoundUtils.addLabelFlags(tree.structure, tree.options);
    } else {
      console.log("tree.options is undefined.");
    }

    if (!tree.moleculeId) {
      tree.moleculeId = CompoundUtils.generateUUID();
    }

    tree.structure = this.addPropertyToStructure(
      tree.structure,
      "moleculeId",
      tree.moleculeId,
    );
    CompoundUtils.addIdsToMolecule(tree.moleculeId, tree.structure);

    if (tree.assign) {
      for (let i = 0; i < tree.assign.length; i++) {
        let assign = tree.assign[i];
        assign.parent = tree.moleculeId;
        if (tree.options) {
          this.addMoleculeOptions(assign.molecule, {
            showHydrogen: tree.options.showHydrogen,
          });
        }
        this.populateTree(assign.molecule);
      }
    }
  }

  private static assertStructureAsObject(
    tree: Compounds.Molecule,
  ): asserts tree is Compounds.HasStructure<Compounds.Molecule> {
    if (!(typeof tree.structure === "object"))
      throw new Error(`expected getStructure() to create structure as object`);
  }

  /**
   * Overrides existing molecule options, or assigns them if no molecule
   * options exist.
   */
  private static addMoleculeOptions(
    molecule: Compounds.Molecule,
    options = {},
  ): Compounds.Molecule {
    molecule.options = molecule.options || {};
    molecule.options = Object.assign(molecule.options, options);
    return molecule;
  }

  /**
   * Merge all tree molecules into a single array of atoms, and a single array
   * of bonds. Mutates the molecule parameter.
   */

  private static flattenTree(
    tree: {
      structure: {
        atoms: ConcatArray<Compounds.Atom>;
        bonds: ConcatArray<Compounds.Bond>;
      };
      hasOwnProperty: (arg0: string) => any;
      assign?: Compounds.Assignment[];
    },
    molecule: Compounds.Structure = { atoms: [], bonds: [] },
    // molecule = { atoms: [], bonds: [] },
  ): Compounds.Structure {
    molecule.atoms = molecule.atoms.concat(tree.structure.atoms);
    molecule.bonds = molecule.bonds.concat(tree.structure.bonds);
    if (tree.assign) {
      for (let i = 0; i < tree.assign.length; i++) {
        const subtree = tree.assign[i].molecule;
        this.assertStructureAsObject(subtree);

        this.flattenTree(subtree, molecule);
      }
    }
    return molecule;
  }

  /**
   * For each of the assignments in the assignment array, perform the following
   * actions on the flattened molecule:
   *    a. Remove the parent atom.
   *    b. Remove the source atom.
   *    c. Remove the bond with the source atom as the target.
   *    d. Update the bond with the parent atom as the target, so that the new
   *        target is the source from the bond in action 2.c (X).
   *    e. Swap all bonds in the source molecule so that X is the source
   *        instead of the target
   *    e. Insert any bonds with the source set to X immediately after the bond
   *        which has X as a target.
   */
  private static makeRenderAssignments(
    tree: Compounds.Molecule,
    flatMol: Compounds.Structure,
  ) {
    if (tree.assign) {
      for (let i = 0; i < tree.assign.length; i++) {
        let a = tree.assign[i];
        if (!a.parent) throw new Error(`unexpected missing a.parent`);
        if (!a.molecule.moleculeId)
          throw new Error(`unexpected missing a.molecule.moleculeId`);
        let parentAtom = a.parentAtom + "-" + a.parent;
        let sourceAtom = a.sourceAtom + "-" + a.molecule.moleculeId;
        CompoundUtils.removeAtom(flatMol, parentAtom);
        CompoundUtils.removeAtom(flatMol, sourceAtom);
        let removedBond = CompoundUtils.removeBondWithTarget(
          flatMol,
          sourceAtom,
        );
        let bondIndex = CompoundUtils.replaceBondTarget(
          flatMol,
          parentAtom,
          removedBond.source,
        );
        CompoundUtils.swapSourceTarget(
          a.molecule.moleculeId,
          removedBond.source,
          flatMol,
        );

        CompoundUtils.orderBonds(removedBond.source, bondIndex, flatMol);
        this.makeRenderAssignments(a.molecule, flatMol);
      }
    }
    return flatMol;
  }
}
